import './Login/Login.scss';

import { clsx } from 'clsx';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Auth } from '../apis/Auth';
import Background from '../components/AuthBackground/AuthBackground';
import GoogleAuthButton from '../components/Oauth/GoogleOauth';
import SpinnerLoader from 'components/Utility/SpinnerLoader';

const getPasswordChecks = (password: string) => [
  { label: 'At least 8 characters', met: password.length >= 8 },
  { label: 'At least 1 uppercase letter', met: /[A-Z]/.test(password) },
  { label: 'At least 1 lowercase letter', met: /[a-z]/.test(password) },
  { label: 'At least 1 digit', met: /\d/.test(password) },
  {
    label: 'At least 1 special character',
    met: /[!@#$%^&*(),.?":{}|<>_\-+=[\]\\;'/`~]/.test(password)
  }
];

const getStrengthLabel = (score: number) => {
  const labels = ['', 'Very Weak', 'Weak', 'Fair', 'Strong', 'Very Strong'];
  return labels[score];
};

const getStrengthColor = (score: number) => {
  const colors = ['', '#ff4d4d', '#ff8c00', '#ffc107', '#4caf50', '#2e7d32'];
  return colors[score];
};

const LoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({
    email: '',
    password: '',
    fullName: '',
    confirmPassword: ''
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loginError, setLoginError] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  const passwordChecks = getPasswordChecks(formData.password);
  const strengthScore = passwordChecks.filter((c) => c.met).length;
  const allPasswordRulesMet = strengthScore === 5;

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  const handleSubmit = async (e: any) => {
    e.preventDefault();
    const formErrors: any = {};
    let hasErrors = false;

    if (!formData.email.trim()) {
      formErrors.email = 'Email is required';
      hasErrors = true;
    }
    if (!formData.fullName.trim()) {
      formErrors.fullName = 'Username is required';
      hasErrors = true;
    }

    if (!formData.password.trim()) {
      formErrors.password = 'Password is required';
      hasErrors = true;
    } else if (!allPasswordRulesMet) {
      formErrors.password = 'Password does not meet all requirements';
      hasErrors = true;
    }

    if (!formData.confirmPassword.trim()) {
      formErrors.confirmPassword = 'Confirm Password is required';
      hasErrors = true;
    }

    if (!hasErrors) {
      if (formData.password.trim() !== formData.confirmPassword.trim()) {
        formErrors.confirmPassword = 'Passwords do not match';
        hasErrors = true;
      }
    }

    if (!hasErrors) {
      const payload = {
        email: formData.email.trim().toLowerCase(),
        full_name: formData.fullName.trim(),
        password: formData.password
      };
      setIsLoading(true);
      const data = await Auth.registerUser(payload);
      setIsLoading(false);

      if (data) {
        // Keep the redirect URL stored for after login
        navigate('/login');
      }
    } else {
      setErrors(formErrors);
      setLoginError(true);
    }
  };

  const showChecklist = passwordFocused || formData.password.length > 0;
  const showStrength = formData.password.length > 0;
  const showMatchFeedback = formData.confirmPassword.length > 0;
  const passwordsMatch = formData.password === formData.confirmPassword;

  return (
    <div className="relative">
      <Background imageUrl="/auth/authback.png" />

      <div className="main-container h-screen register-page-content">
        <div className="mt-[20px] align-center flex justify-center">
          <img width={200} src="/brand-logo/logo.svg" alt="" />
        </div>
        <div className="mt-[20px] align-center flex justify-center">
          <h3 className="AuthHeader Manrope text-white">Create account</h3>
        </div>

        <div className="flex justify-center mt-4">
          <div className="AuthBody flex justify-center">
            <div className="w-[400px]">
              {/* form section */}
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <div className="flex flex-start">
                    <label
                      htmlFor="email"
                      className="Manrope text-sm font-semibold mb-1 InputLabel "
                    >
                      Name
                    </label>
                  </div>
                  <input
                    value={formData.fullName}
                    onChange={handleChange}
                    type="text"
                    id="name"
                    name="fullName"
                    autoComplete="name"
                    className={clsx({
                      'transparent-input w-full border mt-[5px] text-white Input ': true,
                      ErrorInput: loginError
                    })}
                  />
                  <div className="flex flex-start">
                    {errors.fullName && (
                      <div className="text-red-500 text-sm mt-1">{errors.fullName}</div>
                    )}
                  </div>
                </div>
                <div className="mb-4">
                  <div className="flex flex-start">
                    <label
                      htmlFor="email"
                      className="Manrope text-sm font-semibold mb-1 InputLabel "
                    >
                      Email
                    </label>
                  </div>
                  <input
                    value={formData.email}
                    onChange={handleChange}
                    type="email"
                    id="email"
                    name="email"
                    autoComplete="email"
                    className={clsx({
                      'transparent-input w-full border mt-[5px] text-white Input ': true,
                      ErrorInput: loginError
                    })}
                  />
                  <div className="flex flex-start">
                    {errors.email && (
                      <div className="text-red-500 text-sm mt-1">{errors.email}</div>
                    )}
                  </div>
                </div>
                <div className="mb-4">
                  <div className="flex flex-start">
                    <label
                      htmlFor="password"
                      className="Manrope text-white block text-sm font-semibold mb-1 InputLabel mt-[10px]"
                    >
                      Password:
                    </label>
                  </div>
                  <div className="relative">
                    <input
                      value={formData.password}
                      onChange={handleChange}
                      onFocus={() => setPasswordFocused(true)}
                      onBlur={() => setPasswordFocused(false)}
                      type={passwordVisible ? 'text' : 'password'}
                      id="password"
                      name="password"
                      autoComplete="new-password"
                      className="w-full transparent-input border mt-[5px]  text-white Input"
                    />
                    <img
                      onClick={() => setPasswordVisible(!passwordVisible)}
                      className="absolute eyeIcon pointer "
                      src="/auth/Icon.png"
                      alt=""
                    />
                  </div>
                  <div className="flex flex-start">
                    {errors.password && (
                      <div className="text-red-500 text-sm mt-1">{errors.password}</div>
                    )}
                  </div>

                  {showChecklist && (
                    <ul className="password-checklist">
                      {passwordChecks.map((check) => (
                        <li key={check.label} className={check.met ? 'met' : ''}>
                          <span className="check-icon">{check.met ? '\u2713' : '\u25CB'}</span>
                          {check.label}
                        </li>
                      ))}
                    </ul>
                  )}

                  {showStrength && (
                    <div className="strength-meter">
                      <div className="strength-bar-track">
                        <div
                          className="strength-bar-fill"
                          style={{
                            width: `${(strengthScore / 5) * 100}%`,
                            backgroundColor: getStrengthColor(strengthScore)
                          }}
                        />
                      </div>
                      <span
                        className="strength-label"
                        style={{ color: getStrengthColor(strengthScore) }}
                      >
                        {getStrengthLabel(strengthScore)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="mb-4">
                  <div className="flex flex-start">
                    <label
                      htmlFor="confirmPassword"
                      className="Manrope text-white block text-sm font-semibold mb-1 InputLabel mt-[10px]"
                    >
                      Confirm Password:
                    </label>
                  </div>
                  <div className="relative">
                    <input
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      type={confirmPasswordVisible ? 'text' : 'password'}
                      id="confirmPassword"
                      name="confirmPassword"
                      autoComplete="new-password"
                      className="w-full transparent-input border mt-[5px]  text-white Input"
                    />
                    <img
                      onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
                      className="absolute eyeIcon pointer "
                      src="/auth/Icon.png"
                      alt=""
                    />
                  </div>
                  <div className="flex flex-start">
                    {errors.confirmPassword && (
                      <div className="text-red-500 text-sm mt-1">{errors.confirmPassword}</div>
                    )}
                  </div>
                  {showMatchFeedback && (
                    <div
                      className={`password-match-feedback ${passwordsMatch ? 'match' : 'no-match'}`}
                    >
                      {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
                    </div>
                  )}
                </div>
                <SpinnerLoader loading={isLoading}>
                  <button type="submit" className="registrationBtn !text-white w-full mt-8">
                    Registration
                  </button>
                </SpinnerLoader>
              </form>
              <div className="flex justify-between mt-4 items-center gap-[10px] ">
                <div className="splitter"></div>
                <p className="continueText">or Continue with</p>
                <div className="splitter"></div>
              </div>

              <GoogleAuthButton />

              {/* <div className="flex justify-between mt-4 items-center gap-[16px]">
                    <div className="socialContainer">
                        <img className="socialIcon" src="/auth/Apple.png" alt="" />
                    </div>
                    <div className="socialContainer">
                        <img className="socialIcon" src="/auth/Google.png" alt="" />
                    </div>
                    <div className="socialContainer">
                      <img className="socialIcon" src="/auth/facebook.png" alt="" />
                    </div>
              </div> */}
              <div className="mt-4 mb-4">
                <p className="createAccountText">
                  Already have an account?{' '}
                  <a href="/login">
                    <span className="loginText">Log In</span>
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/*
const GoogleAuthButton = () => {
  const handleGoogleAuth = () => {
    console.log("Google Authentication initiated");
  };

  return (
    <button className="px-4 py-2 border flex gap-2 border-slate-200 dark:border-slate-700 rounded-lg text-slate-700 dark:text-slate-200 hover:border-slate-400 dark:hover:border-slate-500 hover:text-slate-900 dark:hover:text-slate-300 hover:shadow transition duration-150">
      <img
        className="w-6 h-6"
        src="https://www.svgrepo.com/show/475656/google-color.svg"
        loading="lazy"
        alt="google logo"
      />
      <span className="text-white">Login with Google</span>
    </button>
  );
};
*/

export default LoginPage;

// import React, { useState } from "react";
// import { Link } from "react-router-dom";
// import { Auth } from "../apis/Auth";
// import { useNavigate } from 'react-router-dom';
// const RegistrationPage = () => {
//   return (
//     <div className="bg-gray-900 text-white flex justify-center items-center h-screen">
//       <div className="max-w-md w-full p-6 bg-gray-800 rounded-lg shadow-lg">
//         <div className="flex justify-center mt-4">
//           <GoogleAuthButton />
//         </div>
//         <span className="mt-2 mb-2">or</span>
//         <RegisterForm  />
//         <p className="mt-4">
//           Already have an account?
//           <span className="text-blue-400">
//             <Link to="/login"> Login</Link>
//           </span>
//         </p>
//       </div>
//     </div>
//   );
// };

// const RegisterForm = () => {
//   const navigate = useNavigate();
//   const [formData, setFormData] = useState({
//     email: "",
//     fullName: "",
//     password: ""
//   });

//   const [errors, setErrors] = useState({
//     email: "",
//     fullName: "",
//     password: ""
//   });

//   const handleChange = (e: any) => {
//     const { name, value } = e.target;
//     setFormData({
//       ...formData,
//       [name]: value
//     });
//   };

//   const handleSubmit = (e: any) => {
//     e.preventDefault();
//     // Validation
//     let formErrors: any = {};
//     let hasErrors = false;

//     if (!formData.email.trim()) {
//       formErrors.username = "Username is required";
//       hasErrors = true;
//     }

//     if (!formData.fullName.trim()) {
//       formErrors.fullName = "Full Name is required";
//       hasErrors = true;
//     }

//     if (!formData.password.trim()) {
//       formErrors.password = "Password is required";
//       hasErrors = true;
//     }

// if (!hasErrors) {
//   Auth.registerUser(
//     {
//       email: formData.email,
//       full_name: formData.fullName,
//       password: formData.password
//     }
//     )
//     .then((res) => {
//       console.log("reg res", res.data);
//       navigate('/login');

//     })
//     .catch((e) => {
//       console.log("err", e.response);
//     });
// }
//     setErrors(formErrors);

//     // Proceed with form submission
//     console.log("Form submitted", formData);
//   };

//   return (
//     <form onSubmit={handleSubmit}>
//       <div className="mb-4">
//         <div className="flex flex-start">
//           <label htmlFor="username" className="text-sm font-semibold mb-1">
//             Email:
//           </label>
//         </div>
//         <input
//           type="email"
//           id="email"
//           name="email"
//           className="w-full border rounded-lg px-3 py-2 bg-gray-700 text-white"
//           value={formData.email}
//           onChange={handleChange}
//         />
//         <div className="flex flex-start">
//           {errors.email && <div className="text-red-500 text-sm mt-1">{errors.email}</div>}
//         </div>
//       </div>
//       <div className="mb-4">
//         <div className="flex flex-start">
//           <label htmlFor="fullName" className="text-sm font-semibold mb-1">
//             Full Name:
//           </label>
//         </div>
//         <input
//           type="text"
//           id="fullName"
//           name="fullName"
//           className="w-full border rounded-lg px-3 py-2 bg-gray-700 text-white"
//           value={formData.fullName}
//           onChange={handleChange}
//         />
//          <div className="flex flex-start">
//           {errors.fullName && <div className="text-red-500 text-sm mt-1">{errors.fullName}</div>}
//          </div>
//       </div>
//       <div className="mb-4">
//         <div className="flex flex-start">
//           <label
//             htmlFor="password"
//             className="block text-sm font-semibold mb-1"
//           >
//             Password:
//           </label>
//         </div>
//         <input
//           type="password"
//           id="password"
//           name="password"
//           className="w-full border rounded-lg px-3 py-2 bg-gray-700 text-white"
//           value={formData.password}
//           onChange={handleChange}
//         />
//          <div className="flex flex-start">
//           {errors.password && <div className="text-red-500 text-sm mt-1">{errors.password}</div>}
//          </div>
//       </div>
//       <button
//         type="submit"
//         className="bg-blue-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-blue-700 pl-16 pr-16 mt-2"
//       >
//         Sign Up
//       </button>
//     </form>
//   );
// };

// const GoogleAuthButton = () => {
//   const handleGoogleAuth = () => {
//     console.log("Google Authentication initiated");
//   };

//   return (
//     <button className="px-4 py-2 border flex gap-2 border-slate-200 dark:border-slate-700 rounded-lg text-slate-700 dark:text-slate-200 hover:border-slate-400 dark:hover:border-slate-500 hover:text-slate-900 dark:hover:text-slate-300 hover:shadow transition duration-150">
//       <img
//         className="w-6 h-6"
//         src="https://www.svgrepo.com/show/475656/google-color.svg"
//         loading="lazy"
//         alt="google logo"
//       />
//       <span className="text-white">SignUp with Google</span>
//     </button>
//   );
// };

// export default RegistrationPage;
